import React from 'react';
import SwModal from '../Common/SwModal/SwModal';
import SwButton from '../Common/SwButtons/SwButtons';
import SwAlert from '../Common/SwAlert/SwAlert';
import SwLoader from '../Common/SwLoader/SwLoader';
import MealPlanReviewTable from './MealPlanReviewTable';
import {
  changedCount,
  changedUserCount,
  groupSelectionByOffers,
  fmtDate,
} from './mealPlanUsersData';

/**
 * MPC-7 — assign the selected employees to the plans on screen.
 *
 * The outcomes shown here come from the endpoint's own dry run (unrestrict with
 * preview:true), not from a guess made on the roster row. That matters: someone
 * the roster reports as unregistered may still be Restricted, and unrestricting
 * them is perfectly valid. Only the endpoint knows, so only the endpoint decides
 * — and where it declines, it says why, and that reason is shown verbatim
 * rather than swallowed (MPC-15).
 *
 * Which plans each employee gets is set in the grid, not assumed: a meal type
 * can be served by several wallet offers, the SPOC may mean only some of them,
 * and not necessarily the same ones for everybody.
 */
const MealPlanAssignModal = ({
  show,
  onClose,
  plan,
  preview,
  rowFor,
  offers = [],
  userIds = [],
  selection = {},
  onSelectionChange,
  widthClass = 'MealPlanUsersModal sw-small-modal ExpandedWidth',
  saving = false,
  onConfirm,
}) => {
  const results = preview?.results || [];

  /* One result per employee x plan. The grid counts changes, the button counts
     people — the same number only while a meal type has one plan. */
  const count = changedCount(results);
  const people = changedUserCount(results);
  const hasSelection = groupSelectionByOffers(selection).length > 0;

  return (
    <SwModal
      show={show}
      onClose={onClose}
      keyboard
      size="sm"
      className={widthClass}
      title={`Assign ${userIds.length} employee${
        userIds.length === 1 ? '' : 's'
      } to ${plan?.name ? `the ${plan.name} plan` : 'the plan'}`}
      footer={
        <>
          <SwButton variant="dark-outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </SwButton>
          {/* The primary action states the exact number of people it acts on. */}
          <SwButton
            variant="primary"
            size="sm"
            loading={saving}
            disabled={!count || !hasSelection}
            onClick={onConfirm}
          >
            {count ? `Assign ${people} employee${people === 1 ? '' : 's'}` : 'Nothing to assign'}
          </SwButton>
        </>
      }
    >
      {preview?.error ? (
        <SwAlert variant="danger" className="mb-0">
          The plan service could not confirm what this would do, so nothing has been
          changed. Close this and try again.
        </SwAlert>
      ) : (
        <>
          {!preview && hasSelection ? (
            <div className="SwLoaderContainer">
              <SwLoader />
            </div>
          ) : null}

          <MealPlanReviewTable
            action="assign"
            offers={offers}
            userIds={userIds}
            rowFor={rowFor}
            selection={selection}
            onSelectionChange={onSelectionChange}
            results={results}
            disabled={saving}
          />

          {/* MPC-7: no points are credited at this moment, and the confirmation
              says when they will be. */}
          {!hasSelection ? (
            <SwAlert
              variant="warning"
              className="mb-0"
              icon={<i className="bi bi-exclamation-triangle-fill" />}
            >
              No plans are selected, so there is nothing to assign.
            </SwAlert>
          ) : count ? (
            <SwAlert variant="info" className="mb-0" icon={<i className="bi bi-info-circle" />}>
              Nothing is written until you confirm. No points are credited now — these
              employees become eligible from the plan&apos;s next top-up cycle
              {plan?.validTo ? `, and the plan runs until ${fmtDate(plan.validTo)}` : ''}.
            </SwAlert>
          ) : preview ? (
            <SwAlert
              variant="warning"
              className="mb-0"
              icon={<i className="bi bi-exclamation-triangle-fill" />}
            >
              Nothing in this selection can be assigned — the reason against each
              employee is above.
            </SwAlert>
          ) : null}
        </>
      )}
    </SwModal>
  );
};

export default MealPlanAssignModal;
